export interface UserProfile {
  id: number;
  full_name: string;
  email: string;
  phone?: string;
  nik?: string;
  nip?: string;
  role: string;
  avatar_url?: string;
  birth_date?: string;
  address?: string;
  position?: string;
  department?: string;
  join_date?: string;
  employee_id?: number;
  created_at: string;
}

export interface UpdateProfileData {
  full_name?: string;
  email?: string;
  phone?: string;
  nik?: string;
  birth_date?: string;
  address?: string;
  position?: string;
  department?: string;
  avatar_url?: string;
}

export interface ChangePasswordData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface UserSettings {
  email_notifications: boolean;
  push_notifications: boolean;
  marketing_emails: boolean;
  security_alerts: boolean;
  weekly_digest: boolean;
  language: string;
  timezone: string;
}

const userApi = {
  // Get user profile
  async getProfile(): Promise<UserProfile> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/user/profile`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to fetch profile' }));
      throw new Error(
        errorData.error || `Failed to fetch profile: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  },

  // Update user profile
  async updateProfile(data: UpdateProfileData): Promise<UserProfile> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    console.log('Sending profile update data:', data);

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/user/profile`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `Failed to update profile: ${response.status} ${response.statusText}`,
      }));
      console.error('Profile update error:', {
        status: response.status,
        statusText: response.statusText,
        errorData,
      });
      throw new Error(
        errorData.error || `Failed to update profile: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  },

  // Change password
  async changePassword(data: {
    current_password: string;
    new_password: string;
  }): Promise<{ success: boolean; message: string }> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/user/password`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to change password' }));
      throw new Error(
        errorData.error || `Failed to change password: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  },

  // Update settings
  async updateSettings(
    settings: UserSettings
  ): Promise<{ success: boolean; message: string; settings: UserSettings }> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/user/settings`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to update settings' }));
      throw new Error(
        errorData.error || `Failed to update settings: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  },

  // Get current user (alias for getProfile)
  async getCurrentUser(): Promise<UserProfile> {
    return this.getProfile();
  },
};

export default userApi;
